import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { truckingReportExtAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
};

const fmtLong = (dateStr) =>
  new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });

const emptyData = { groups: [], summary: null, storeInfo: {} };

export default function PSACCBillingPrintPage() {
  const [searchParams] = useSearchParams();
  const dateFrom   = searchParams.get('dateFrom') || new Date().toISOString().split('T')[0];
  const dateTo     = searchParams.get('dateTo') || dateFrom;
  const soaNumber  = searchParams.get('soaNumber') || '';

  const [data, setData]       = useState(emptyData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    truckingReportExtAPI.getPSACCBilling({ dateFrom, dateTo, soaNumber })
      .then((res) => {
        setData({
          groups:    res.data?.groups || [],
          summary:   res.data?.summary || null,
          storeInfo: res.data?.storeInfo || {},
        });
        setLoading(false);
        setTimeout(() => window.print(), 400);
      })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    const label = dateFrom === dateTo ? fmtLong(dateFrom) : `${dateFrom} to ${dateTo}`;
    document.title = `PSACC Billing Report — ${label}`;
  }, [dateFrom, dateTo]);

  if (loading) return <p style={{ padding: 40 }}>Loading report…</p>;

  const { groups, summary, storeInfo } = data;
  const companyName = storeInfo.companyName || 'ARVANNAH RENZ TRUCKING INC.';
  const companyAddress = storeInfo.address || 'Q. ABETO ST. BRGY. ABETO-MIRASOL TAFT SOUTH MANDURIAO, ILOILO CITY';
  const companyTin = storeInfo.tin ? `TIN NO. ${storeInfo.tin}` : 'TIN NO. 768-950-988-000-VAT';

  return (
    <>
      <style>{`
        body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        .page { display: block; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        th { background: #f0f0f0; border: 1px solid #999; padding: 3px 6px; text-align: left; font-size: 8.5pt; }
        td { border: 1px solid #999; padding: 3px 6px; }
        .company-header { text-align: center; border: none !important; padding: 2px 0 6px !important; }
        .company-header .name { font-weight: 700; font-size: 11.5pt; }
        .company-header .line { font-size: 8.5pt; }
        .billing-header { background: #cceae7; }
        .billing-header .soa-block { font-weight: 700; font-size: 9pt; }
        .billing-header .meta { font-size: 8pt; color: #333; margin-top: 2px; }
        .span-header { background: #eef7f6; font-weight: 600; }
        .billing-subtotal { background: #e8e8e8; font-weight: 700; }
        .spacer-row td { border: none; padding: 3px 0; }
        .merged-total { text-align: center; vertical-align: middle; font-weight: 600; }
        .voided-badge { color: #b91c1c; margin-left: 6px; font-weight: 700; }
        /* One Billing group per printed page, except the very first. */
        .billing-break { page-break-before: always; break-before: page; }
        .summary-block { display: flex; justify-content: flex-end; margin-top: 16px; }
        .summary-block table { width: auto; }
        .summary-block td { border: none; padding: 1px 8px; }
        .sig-block { display: flex; justify-content: space-between; font-size: 9.5pt; margin-top: 40px; }
      `}</style>

      <div className="page">
        {/* Addressed-to + Date — appears once, before the table. */}
        

        <table>
          {/* thead repeats on every printed page — company header + column headers. */}
          <thead>
            <tr>
              <td colSpan={10} className="company-header">
                <div className="name">{companyName}</div>
                <div className="line">{companyAddress}</div>
                <div className="line">{companyTin}</div>
                <div className="line" style={{ fontWeight: 600, marginTop: 2 }}>
                  STATEMENT OF ACCOUNT NO. {soaNumber || '___________'}
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={10} className="company-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5pt', marginBottom: 8 }}>
                  <div>
                    <p style={{ fontWeight: 600, margin: 0 }}>PHILIPPINE SPAN ASIA CARRIER CORP.</p>
                    <p style={{ margin: 0 }}>Coastal Road, Brgy. Balabago, Jaro, Iloilo City</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0 }}>Date: <strong>{fmtDate(new Date())}</strong></p>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <th>DATE</th>
              <th>PLATE NO.</th>
              <th>WB NO.</th>
              <th>VESSEL / VOY. NO.</th>
              <th>VAN NO.</th>
              <th>CONSIGNEE</th>
              <th>LOCATION</th>
              <th style={{ textAlign: 'right' }}>RATES</th>
              <th style={{ textAlign: 'right' }}>TOTAL AMOUNT PER SPAN</th>
              <th>DATE RETURN</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 16 }}>No billing records found for this range.</td></tr>
            ) : groups.map((g, gi) => (
              <React.Fragment key={g.billId}>
                {/* Billing header — SOA/date for this bill. 
                <tr className={`billing-header ${gi > 0 ? 'billing-break' : ''}`}>
                  <td colSpan={10}>
                    <div className="soa-block">
                      <div>STATEMENT OF ACCOUNT NO. {g.soaNumber || g.billName}</div>
                      <div>Date: {fmtDate(g.billDate)}</div>
                    </div>
                    <div className="meta">
                      Billing: {g.billName} — {(g.status || 'draft').toUpperCase()}
                      {g.isVoided && <span className="voided-badge">VOIDED</span>}
                      {' '}({g.waybillCount} waybill{g.waybillCount === 1 ? '' : 's'})
                    </div>
                  </td>
                </tr>*/}

                {(g.spans || []).length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', fontStyle: 'italic' }}>No waybills attached.</td></tr>
                ) : g.spans.map((span, si) => (
                  <React.Fragment key={si}>
                    {span.rows.map((r, ri) => (
                      <tr key={ri}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                        <td>{r.plateNo}</td>
                        <td>{r.wbNo}</td>
                        <td>{r.vesselVoyage}</td>
                        <td>{r.vanNo}</td>
                        <td>{r.consignee}</td>
                        <td>{r.location}</td>
                        <td style={{ textAlign: 'right' }}>₱{PHP(r.total)}</td>
                        {/* Merged cell: TOTAL AMOUNT PER SPAN appears once per
                            span, vertically centered across its rows. */}
                        {ri === 0 && (
                          <td style={{ textAlign: 'right' }} className="merged-total" rowSpan={span.rows.length}>
                            Php {PHP(span.subtotal.total)}
                          </td>
                        )}
                        <td>{r.dateReturn ? fmtDate(r.dateReturn) : '—'}</td>
                      </tr>
                    ))}
                    {si < g.spans.length - 1 && (
                      <tr className="spacer-row"><td colSpan={10}>&nbsp;</td></tr>
                    )}
                  </React.Fragment>
                ))}

                <tr className="billing-subtotal">
                  <td colSpan={8} style={{ textAlign: 'right' }}>
                    SUBTOTAL — {g.billName} ({g.waybillCount} waybill{g.waybillCount === 1 ? '' : 's'}):
                  </td>
                  <td style={{ textAlign: 'right' }}>₱{PHP(g.subtotal.total)}</td>
                  <td></td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {summary && groups.length > 0 && (
          <div className="summary-block">
            <table>
              <tbody>
                <tr><td style={{ textAlign: 'right', color: '#555' }}>TOTAL:</td><td style={{ textAlign: 'right', fontWeight: 600 }}>₱{PHP(summary.grandTotal)}</td></tr>
                <tr><td style={{ textAlign: 'right', color: '#555' }}>VAT:</td><td style={{ textAlign: 'right', fontWeight: 600 }}>₱{PHP(summary.vat)}</td></tr>
                <tr><td style={{ textAlign: 'right', color: '#555' }}>Net of VAT:</td><td style={{ textAlign: 'right', fontWeight: 600 }}>₱{PHP(summary.netOfVAT)}</td></tr>
                <tr><td style={{ textAlign: 'right', color: '#555' }}>CWT:</td><td style={{ textAlign: 'right', fontWeight: 600 }}>₱{PHP(summary.cwt)}</td></tr>
                <tr style={{ borderTop: '2px solid #000' }}>
                  <td style={{ textAlign: 'right', fontWeight: 700, paddingTop: 4 }}>TOTAL AMOUNT DUE:</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, paddingTop: 4 }}>₱{PHP(summary.amountDue)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="sig-block">
          <div>
            <p style={{ fontWeight: 600, borderTop: '1px solid #000', paddingTop: 4, paddingRight: 64, margin: 0 }}>JOHN EFREN OCTAVIANO</p>
            <p style={{ margin: '2px 0 0', fontSize: '9pt', color: '#555' }}>CHIEF FINANCIAL OFFICER</p>
          </div>
          <div>
            <p style={{ margin: '0 0 16px' }}>RECEIVED BY: <span style={{ display: 'inline-block', borderBottom: '1px solid #000', width: 180 }}>&nbsp;</span></p>
            <p style={{ margin: 0 }}>Date: <span style={{ display: 'inline-block', borderBottom: '1px solid #000', width: 180 }}>&nbsp;</span></p>
          </div>
        </div>
      </div>

      <div className="no-print" style={{ marginTop: 16 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px' }}>Print</button>
      </div>
    </>
  );
}
